import { createTheme, alpha } from '@mui/material/styles';

export const createVibrantTheme = (mode = 'light') => {
  // Updated color palette based on purple theme
  const primaryColor = mode === 'light' ? '#6a85b6' : '#8b6cb5'; // Primary blue-purple
  const secondaryColor = mode === 'light' ? '#8b6cb5' : '#b39ddb'; // Secondary purple
  
  // Common colors used across components (including PageHeader)
  const commonColors = {
    // Colors for access level chips
    accessLevels: {
      READ: {
        text: mode === 'light' ? '#1565c0' : 'rgba(179, 229, 252, 0.9)',
        border: 'rgba(66, 165, 245, 1)',
        bg: mode === 'light' ? 'rgba(66, 165, 245, 0.1)' : 'rgba(25, 118, 210, 0.2)'
      },
      WRITE: {
        text: mode === 'light' ? '#2e7d32' : 'rgba(185, 246, 202, 0.9)',
        border: 'rgba(102, 187, 106, 1)',
        bg: mode === 'light' ? 'rgba(102, 187, 106, 0.1)' : 'rgba(76, 175, 80, 0.2)'
      },
      ADMIN: {
        text: mode === 'light' ? '#c62828' : 'rgba(255, 205, 210, 0.9)',
        border: 'rgba(239, 83, 80, 1)',
        bg: mode === 'light' ? 'rgba(239, 83, 80, 0.1)' : 'rgba(198, 40, 40, 0.2)'
      }
    },
    // Colors for search highlighting
    search: {
      highlight: mode === 'light' ? 'rgba(139, 108, 181, 0.2)' : 'rgba(139, 108, 181, 0.3)', // Purple highlight
      active: mode === 'light' ? 'rgba(139, 108, 181, 0.4)' : 'rgba(139, 108, 181, 0.5)', // Purple active
      text: mode === 'light' ? '#673ab7' : '#d1c4e9', // Purple text
      border: '#8b6cb5' // Purple border
    },
    // Colors for folder/file icons
    icons: {
      folder: mode === 'light' ? '#673ab7' : '#b39ddb', // Purple for folders
      video: mode === 'light' ? '#5c6bc0' : '#9fa8da', // Indigo for videos
      action: mode === 'light' ? '#8b6cb5' : '#c5a3ff', // Light purple for action buttons
      default: mode === 'light' ? '#616161' : '#e0e0e0' // Gray default
    },
    // Login page specific colors
    login: {
      background: mode === 'light'
        ? 'linear-gradient(135deg, #f5f7fb 0%, #e8effc 50%, #e0d9f6 100%)'
        : 'linear-gradient(135deg, #1a1a2e 0%, #262645 50%, #2c2155 100%)',
      cardBg: mode === 'light'
        ? 'rgba(255, 255, 255, 0.85)'
        : 'rgba(30, 30, 45, 0.7)',
      cardBorder: mode === 'light'
        ? 'rgba(106, 133, 182, 0.1)'
        : 'rgba(139, 108, 181, 0.15)',
      inputBorder: mode === 'light'
        ? 'rgba(106, 133, 182, 0.3)'
        : 'rgba(139, 108, 181, 0.3)',
      buttonGradient: mode === 'light'
        ? 'linear-gradient(135deg, #6a85b6 0%, #8b6cb5 100%)'
        : 'linear-gradient(135deg, #8b6cb5 0%, #a989d5 100%)'
    },
    // Admin page specific colors
    admin: {
      background: mode === 'light'
        ? '#f5f7fb'
        : '#1a1a2e',
      cardBg: mode === 'light'
        ? '#ffffff'
        : '#2c2c3c',
      navbarGradient: mode === 'light'
        ? 'linear-gradient(90deg, #6a85b6 0%, #8b6cb5 100%)'
        : 'linear-gradient(90deg, #3f4e63 0%, #575872 100%)',
      tableBgHover: mode === 'light'
        ? 'rgba(139, 108, 181, 0.08)'
        : 'rgba(139, 108, 181, 0.16)',
      dashboardCardBg: mode === 'light'
        ? 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
        : 'linear-gradient(135deg, #2c2c3c 0%, #383850 100%)'
    }
  };
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
        light: mode === 'light' ? '#8aa0d4' : '#a989d5',
        dark: mode === 'light' ? '#4c6ba3' : '#6a4c9c',
        contrastText: '#ffffff',
      },
      secondary: {
        main: secondaryColor,
        light: mode === 'light' ? '#ab8fd3' : '#d1c4e9',
        dark: mode === 'light' ? '#6a4c9c' : '#9575cd',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#ffb300', // Amber
        light: '#ffe54c',
        dark: '#c68400',
      },
      success: {
        main: '#43a047', // Green
        light: '#76d275',
        dark: '#00701a',
      },
      error: {
        main: '#e53935', // Red
        light: '#ff6f60',
        dark: '#ab000d',
      },
      info: {
        main: '#5c6bc0', // Indigo
        light: '#8e99f3',
        dark: '#26418f',
      },
      background: {
        default: mode === 'light' ? '#f5f7fb' : '#1a1a2e', // Lighter blue-ish white / Dark navy
        paper: mode === 'light' ? '#ffffff' : '#2c2c3c', // White / Dark slate
      },
      text: {
        primary: mode === 'light' ? '#2c3e50' : '#e8eaf6',
        secondary: mode === 'light' ? '#546e7a' : '#b0bec5',
      },
      // Add searchHighlight to the theme palette for easy access
      searchHighlight: {
        bg: commonColors.search.highlight,
        activeBg: commonColors.search.active,
        text: commonColors.search.text,
        border: commonColors.search.border
      },
      // Add accessLevels to theme palette
      accessLevels: commonColors.accessLevels,
      // Add icon colors to theme palette
      iconColors: commonColors.icons,
      // Add login colors
      login: commonColors.login,
      // Add admin colors
      admin: commonColors.admin
    },
    typography: {
      // Using Poppins to match SRMD.org
      fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      // Make headings more compact
      h1: { 
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 700,
        letterSpacing: '-0.01em',
        marginBottom: '0.5rem' // Reduced margin
      },
      h2: { 
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 700,
        letterSpacing: '-0.01em',
        marginBottom: '0.5rem' // Reduced margin
      },
      h3: { 
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        marginBottom: '0.4rem' // Reduced margin
      },
      h4: { 
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        marginBottom: '0.3rem' // Reduced margin
      },
      h5: { 
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        marginBottom: '0.2rem' // Reduced margin
      },
      h6: { 
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        marginBottom: '0.2rem' // Reduced margin
      },
      // Make body text more compact
      body1: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 400,
        lineHeight: 1.5, // Slightly reduced from 1.65
      },
      body2: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 400,
        lineHeight: 1.5, // Slightly reduced from 1.65
      },
      button: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.02em',
      },
      caption: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 400,
      },
      overline: {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    // Custom spacing system - overriding MUI's default spacing
    spacing: factor => `${0.6 * factor}rem`, // Reduced from 0.75 to 0.6 for more compact spacing
    components: {
      // Make general spacing more compact in all components
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: '12px', // Reduced from 16px
            paddingRight: '12px', // Reduced from 16px
            [theme => theme.breakpoints.up('sm')]: {
              paddingLeft: '16px', // Reduced from 24px
              paddingRight: '16px', // Reduced from 24px
            },
          }
        }
      },
      // Reduce card padding
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12, // Reduced from 16
            boxShadow: mode === 'light'
              ? '0 6px 16px rgba(106, 133, 182, 0.08)' // Reduced shadow
              : '0 6px 16px rgba(30, 30, 46, 0.2)', // Reduced shadow
            overflow: 'hidden',
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'translateY(-3px)', // Reduced from -5px
              boxShadow: mode === 'light'
                ? '0 10px 25px rgba(106, 133, 182, 0.15)' // Reduced shadow
                : '0 10px 25px rgba(0, 0, 0, 0.25)', // Reduced shadow
            },
          }
        }
      },
      // Make card content more compact
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '12px', // Reduced from 16px
            '&:last-child': {
              paddingBottom: '12px', // Reduced from 16px
            },
            [theme => theme.breakpoints.up('sm')]: {
              padding: '16px', // Reduced from 20px
              '&:last-child': {
                paddingBottom: '16px', // Reduced from 20px
              },
            },
          }
        }
      },
      // Make paper components more compact
      MuiPaper: {
        styleOverrides: {
          root: {
            padding: '12px', // Reduced from 16px
            [theme => theme.breakpoints.up('sm')]: {
              padding: '16px', // Reduced from 20px
            },
            '&.admin-dashboard-card': {
              background: commonColors.admin.dashboardCardBg,
              borderRadius: 12, // Reduced from 16
              boxShadow: mode === 'light'
                ? '0 6px 16px rgba(106, 133, 182, 0.08)' // Reduced shadow
                : '0 6px 16px rgba(30, 30, 46, 0.2)', // Reduced shadow
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-3px)', // Reduced from -5px
                boxShadow: mode === 'light'
                  ? '0 10px 25px rgba(106, 133, 182, 0.15)' // Reduced shadow
                  : '0 10px 25px rgba(0, 0, 0, 0.25)', // Reduced shadow
              }
            }
          }
        }
      },
      // Update AppBar to be more compact
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: mode === 'light' 
              ? 'linear-gradient(135deg, #6a85b6 0%, #8b6cb5 50%, #baa5ff 100%)'
              : 'linear-gradient(135deg, #3f4e63 0%, #575872 50%, #7a5667 100%)',
            color: '#ffffff',
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.12)', // Reduced shadow
            borderRadius: '8px', // Reduced from 12px
            margin: '0 8px 8px 8px', // Reduced from 12px
            width: 'auto'
          },
        },
      },
      // Make toolbar more compact
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '48px', // Reduced from 56px
            [theme => theme.breakpoints.up('sm')]: {
              minHeight: '56px', // Reduced from 60px
            },
            padding: '0 12px', // Reduced from 16px
            [theme => theme.breakpoints.up('sm')]: {
              padding: '0 16px', // Reduced from 20px
            },
          },
          // Ensure that dense toolbars are even more compact
          dense: {
            minHeight: '40px', // Reduced from 48px
            [theme => theme.breakpoints.up('sm')]: {
              minHeight: '44px', // Reduced from 52px
            },
          },
        },
      },
      // Make buttons more compact
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6, // Reduced from 8
            padding: '6px 12px', // Reduced from 8px 16px
            fontWeight: 600,
            transition: 'all 0.25s', // Reduced from 0.3s
            '&:hover': {
              transform: 'translateY(-1px)', // Reduced from -2px
              boxShadow: mode === 'light' 
                ? '0 4px 12px rgba(106, 133, 182, 0.15)' // Reduced shadow 
                : '0 4px 12px rgba(139, 108, 181, 0.2)', // Reduced shadow
            },
            // Make small buttons even more compact
            '&.MuiButton-sizeSmall': {
              padding: '3px 8px', // Reduced from 4px 10px
              fontSize: '0.75rem', // Reduced from 0.8125rem
            },
          },
          contained: {
            background: mode === 'light'
              ? `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
              : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            '&:hover': {
              background: mode === 'light'
                ? `linear-gradient(135deg, #5c78af 0%, #7a59a8 100%)`
                : `linear-gradient(135deg, #7a59a8 0%, #a989d5 100%)`,
            },
          },
          containedSecondary: {
            background: mode === 'light'
              ? `linear-gradient(135deg, ${secondaryColor} 0%, #9c7ad3 100%)`
              : `linear-gradient(135deg, ${secondaryColor} 0%, #c5a3ff 100%)`,
          },
        },
        variants: [
          {
            props: { variant: 'vsCodeBlue' },
            style: {
              backgroundColor: '#152f73', // Keep VS Code blue for consistency
              color: '#ffffff',
              height: '35px',
              border: 'none', 
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#004780',
                transform: 'none',
                boxShadow: 'none',
              },
              '&:disabled': {
                opacity: 0.5,
                pointerEvents: 'none',
              }
            }
          }
        ]
      },
      // Make form fields more compact
      MuiTextField: {
        defaultProps: {
          margin: 'dense',
          size: 'small',
        },
        styleOverrides: {
          root: {
            marginTop: '6px', // Reduced from 8px
            marginBottom: '6px', // Reduced from 8px
            '& .MuiInputBase-root': {
              padding: '4px 8px', // More compact padding
            },
            // Keep search highlight styling
          }
        }
      },
      // Keep using small selects
      MuiSelect: {
        defaultProps: {
          size: 'small',
        }
      },
      // Make form control more compact
      MuiFormControl: {
        styleOverrides: {
          root: {
            marginTop: '6px', // Reduced from 8px
            marginBottom: '6px', // Reduced from 8px
          }
        }
      },
      // Make dialogs more compact
      MuiDialog: {
        styleOverrides: {
          paper: {
            padding: '8px', // Reduced from 12px
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '8px 12px', // Reduced from 12px 16px
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '6px 12px', // Reduced from 8px 16px
            '&:first-of-type': {
              paddingTop: '8px', // Reduced from 12px
            }
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '6px 12px', // Reduced from 8px 16px
          }
        }
      },
      // Make lists more compact
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: '2px', // Reduced from 4px
            paddingBottom: '2px', // Reduced from 4px
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingTop: '4px', // Reduced from 6px
            paddingBottom: '4px', // Reduced from 6px
          }
        }
      },
      // Reduce menu item padding
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: '32px', // Reduced from 40px
            paddingTop: '4px', // Reduced from 6px
            paddingBottom: '4px', // Reduced from 6px
          }
        }
      },
      // Styling for login page (more compact)
      MuiCssBaseline: {
        styleOverrides: {
          // Reduce login card padding
          '.login-card': {
            background: commonColors.login.cardBg,
            backdropFilter: 'blur(8px)',
            borderRadius: 12, // Reduced from 16
            border: `1px solid ${commonColors.login.cardBorder}`,
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)', // Reduced from 32px
            padding: '16px', // Reduced from 24px
            position: 'relative',
            zIndex: 1
          },
          // Add global styles to reduce spacing in all components
          'body': {
            '& .MuiBox-root': {
              margin: '0',
            },
            '& .MuiTypography-root': {
              marginBottom: '0.25em', // Reduced from 0.35em
            },
            '& .MuiCardHeader-root': {
              padding: '8px 12px', // Reduced from 12px 16px
            },
            // Add more global spacing reductions
            '& .MuiAccordionSummary-content': {
              margin: '8px 0', // Reduced from 12px
            },
            '& .MuiCardActions-root': {
              padding: '6px', // Reduced padding
            }
          }
        }
      },
      // Reduce spacing in table rows
      MuiTableRow: {
        styleOverrides: {
          root: {
            '& > .MuiTableCell-root': {
              padding: '6px 12px', // Reduced from 8px 16px
            },
            '&:hover': {
              backgroundColor: commonColors.admin.tableBgHover
            }
          }
        }
      },
      // Add TableCell styling for more compact tables
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '6px 12px', // Reduced padding
          },
          head: {
            fontWeight: 600,
            fontSize: '0.8rem', // Smaller font for headers
          },
          body: {
            fontSize: '0.8rem', // Smaller font for body
          }
        }
      },
      // Make tabs more compact
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: '40px', // Reduced from 48px
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: '40px', // Reduced from 48px
            padding: '4px 12px', // Reduced from 6px 16px
            fontSize: '0.8rem', // Smaller font size
          }
        }
      },
      // Make accordion more compact
      MuiAccordion: {
        styleOverrides: {
          root: {
            margin: '6px 0', // Reduced from 8px
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: '40px', // Reduced from 48px
            padding: '0 12px', // Reduced from 0 16px
          },
          content: {
            margin: '8px 0', // Reduced from 12px
          }
        }
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '6px 12px 12px', // Reduced from 8px 16px 16px
          }
        }
      },
      // Reduce Typography margins
      MuiTypography: {
        styleOverrides: {
          root: {
            marginBottom: '0.25em', // Reduced margin
          },
          paragraph: {
            marginBottom: '0.5em', // Reduced from default
          }
        }
      },
      // Make Chips more compact
      MuiChip: {
        styleOverrides: {
          root: {
            height: '24px', // Reduced height
            fontSize: '0.75rem', // Smaller font
          },
          sizeSmall: {
            height: '20px', // Even smaller
          },
          label: {
            padding: '0 8px', // Reduced padding
          },
          colorPrimary: {
            background: mode === 'light'
              ? `linear-gradient(135deg, ${primaryColor} 0%, #8b6cb5 100%)`
              : primaryColor,
          },
          colorSecondary: {
            background: mode === 'light'
              ? `linear-gradient(135deg, ${secondaryColor} 0%, #9c7ad3 100%)`
              : secondaryColor,
          }
        },
        variants: [
          {
            props: { variant: 'accessLevel', color: 'read' },
            style: {
              backgroundColor: commonColors.accessLevels.READ.bg,
              color: commonColors.accessLevels.READ.text,
              border: `1px solid ${commonColors.accessLevels.READ.border}`,
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 1, fontSize: '0.7rem', fontWeight: 'bold' }
            }
          },
          {
            props: { variant: 'accessLevel', color: 'write' },
            style: {
              backgroundColor: commonColors.accessLevels.WRITE.bg,
              color: commonColors.accessLevels.WRITE.text,
              border: `1px solid ${commonColors.accessLevels.WRITE.border}`,
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 1, fontSize: '0.7rem', fontWeight: 'bold' }
            }
          },
          {
            props: { variant: 'accessLevel', color: 'admin' },
            style: {
              backgroundColor: commonColors.accessLevels.ADMIN.bg,
              color: commonColors.accessLevels.ADMIN.text,
              border: `1px solid ${commonColors.accessLevels.ADMIN.border}`,
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 1, fontSize: '0.7rem', fontWeight: 'bold' }
            }
          }
        ]
      
    },
    // Custom variables for the theme
    customVars: {
      searchHighlight: {
        backgroundColor: commonColors.search.highlight,
        activeBgColor: commonColors.search.active,
        textColor: commonColors.search.text,
        borderColor: commonColors.search.border,
        borderRadius: '2px',
        padding: '0 2px',
        margin: '0 -2px',
      },
      accessLevels: commonColors.accessLevels,
      icons: commonColors.icons,
      login: commonColors.login,
      admin: commonColors.admin
    },
    }
  })
};

// Update helper function to match new color scheme
export const getVibrantIconColor = (type, darkMode) => {
  switch (type) {
    case 'folder':
      return darkMode ? '#b39ddb' : '#673ab7'; // Purple for folders
    case 'video':
      return darkMode ? '#9fa8da' : '#5c6bc0'; // Indigo for videos
    case 'action':
      return darkMode ? '#c5a3ff' : '#8b6cb5'; // Purple for action buttons
    default:
      return darkMode ? '#e0e0e0' : '#616161'; // Gray default
  }
};

// Keep existing helper functions
export const getVibrantChipTextColor = (access, darkMode) => {
  switch (access) {
    case 'READ':
      return darkMode ? 'rgba(179, 229, 252, 0.9)' : '#1565c0'; // Blue
    case 'WRITE': 
      return darkMode ? 'rgba(185, 246, 202, 0.9)' : '#2e7d32'; // Green
    case 'ADMIN':
      return darkMode ? 'rgba(255, 205, 210, 0.9)' : '#c62828'; // Red
    default:
      return darkMode ? 'rgba(224, 224, 224, 0.9)' : '#424242'; // Gray
  }
};

export const getVibrantChipBorderColor = (access) => {
  switch (access) {
    case 'READ':
      return 'rgba(66, 165, 245, 1)'; // Blue border
    case 'WRITE': 
      return 'rgba(102, 187, 106, 1)'; // Green border
    case 'ADMIN':
      return 'rgba(239, 83, 80, 1)'; // Red border
    default:
      return 'rgba(189, 189, 189, 1)'; // Gray border
  }
};

export const getVibrantChipBgColor = (access, darkMode) => {
  switch (access) {
    case 'READ':
      return darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(66, 165, 245, 0.1)'; 
    case 'WRITE': 
      return darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(102, 187, 106, 0.1)'; 
    case 'ADMIN':
      return darkMode ? 'rgba(198, 40, 40, 0.2)' : 'rgba(239, 83, 80, 0.1)'; 
    default:
      return darkMode ? 'rgba(117, 117, 117, 0.2)' : 'rgba(189, 189, 189, 0.1)'; 
  }
};